import * as Bridge from '@/api/BridgeApi'
import * as Layer from '@/components/common/layer'
import * as Definition from '@/components/common/list/DefinitionList'
import {
  LOG_EVENT_DISBURSAL_VALUE,
  LOG_EVENT_NAME,
  WEB_PAGE_NATIVE_ROUTES,
} from '@/domains/CommonDomain'
import { useTrans } from '@/hooks/useTrans'
import { IRootState } from '@/modules'
import { ILayerState, LAYER, layerActions } from '@/modules/common/layer'
import {
  schedulePaymentCancelAsync,
  schedulePaymentDetailSelector,
} from '@/modules/schedule-payment/SchedulePaymentDetailModule'
import {
  billPaymentAmountSelector,
  BILL_PAYMENT_AMOUNT,
  IBillPaymentAmountState,
} from '@/modules/version/V2/bill-payment/BillPaymentAmountModule'
import {
  billPaymentResultSelector,
  IPayAction,
  payAsyncV3,
} from '@/modules/version/V2/bill-payment/BillPaymentResultModule'
import { getRupiahFormat } from '@/utils/currencyUtils'
import { goBack } from '@/utils/historyUtils'
import af from '@/utils/trackingUtils'
import React, { MouseEventHandler, ReactNode, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Action, ActionFunctionAny } from 'redux-actions'
import { CheckBox } from '../../input'
import { Alert } from './Alert'
import { Message } from './Message'
import { Title } from './Title'

interface IAlertLayerProps {
  handler: MouseEventHandler
  rightButtonText?: string
  isConfirm?: boolean
  isType2?: boolean
  isType3?: boolean
  title: string
  children?: ReactNode
  type: ALERT_LAYER_TYPE
}

export enum ALERT_LAYER_TYPE {
  CANCEL = 'CANCEL',
  NEW_ADDRESS = 'NEW_ADDRESS',
  REPORT_CARD = 'REPORT_CARD',
  CANCEL_SCHEDULE_PAYMENT = 'CANCEL_SCHEDULE_PAYMENT',
  SCHEDULE_PAYMENT_SAME_ACCOUNTS = 'SCHEDULE_PAYMENT_SAME_ACCOUNTS',
  BILL_PAYMENT_USE_QUICK_CREDIT = 'BILL_PAYMENT_USE_QUICK_CREDIT',
  BILL_PAYMENT_LOAN_DISBURSAL_CONFIRM = 'BILL_PAYMENT_LOAN_DISBURSAL_CONFIRM',
}

const AlertLayer = (props: IAlertLayerProps) => {
  const $ = useTrans()
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[props.type]
  const dispatch = useDispatch()
  const closeLayer = useCallback(() => dispatch(layerActions.close(props.type)), [
    dispatch,
    props.type,
  ])
  const { rightButtonText, isConfirm = false, isType3 = false, isType2 = false } = props

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition>
      <Alert isOpen={isOpen} isType2={isType2}>
        <Title>{props.title}</Title>
        {props.children ? <Message>{props.children}</Message> : <></>}
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={closeLayer}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isConfirm={isConfirm} isType3={isType3} onClick={props.handler}>
            {rightButtonText || 'Confirm'}
          </Layer.Button>
        </Layer.ButtonBox>
      </Alert>
    </Layer.Wrapper>
  )
}

export const AlertLayerCancelSchedulePayment = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const scheduledTransfer = useSelector(schedulePaymentDetailSelector.scheduledTransfer)

  const onClick = useCallback(() => {
    dispatch(layerActions.close(ALERT_LAYER_TYPE.CANCEL_SCHEDULE_PAYMENT))
    dispatch(schedulePaymentCancelAsync.post({ refNo: scheduledTransfer.refNo }))
  }, [dispatch, scheduledTransfer.refNo])

  return (
    <AlertLayer
      type={ALERT_LAYER_TYPE.CANCEL_SCHEDULE_PAYMENT}
      title={$('schedulepayment_detail_closepopup_desc')}
      handler={onClick}
      rightButtonText={$('schedulepayment_detail_closepopup_close_button')}
      isType3
    />
  )
}

export const AlertLayerNewAddress = (props: { handler: MouseEventHandler }) => {
  const $ = useTrans()

  return (
    <AlertLayer
      type={ALERT_LAYER_TYPE.NEW_ADDRESS}
      title={$('debitcard_shipaddress_etc_enternew_popup_title_alert')}
      handler={props.handler}
      isConfirm
    >
      {$('debitcard_shipaddress_etc_enternew_popup_desc_alert')}
    </AlertLayer>
  )
}

export const AlertLayerReportCard = (props: { handler: MouseEventHandler }) => {
  const $ = useTrans()

  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[ALERT_LAYER_TYPE.REPORT_CARD]

  useEffect(() => {
    Bridge.onBackPressed(() => {
      if (!isOpen) {
        goBack()
      }
    })
  }, [isOpen])

  return (
    <AlertLayer
      type={ALERT_LAYER_TYPE.REPORT_CARD}
      title={$('debitcard_mycard_main_security_reportcard_popup_title_alert')}
      handler={props.handler}
      isType3
      rightButtonText={$('debitcard_mycard_main_security_report_popup_button')}
      isType2
    >
      {$('debitcard_mycard_main_security_reportcard_popup_desc_alert')}
    </AlertLayer>
  )
}

export const AlertLayerSameAccounts = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const type = ALERT_LAYER_TYPE.SCHEDULE_PAYMENT_SAME_ACCOUNTS
  const onClose = useCallback(() => dispatch(layerActions.close(type)), [dispatch, type])
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[type]

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose} useTransition>
      <Alert isOpen={isOpen}>
        <Title>{$('transfer_sameaccount_errorpopup2_alert')}</Title>
        <Layer.ButtonBox>
          <Layer.Button isType3 onClick={onClose}>
            {$('common_confirm_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Alert>
    </Layer.Wrapper>
  )
}

export const AlertLayerBillPaymentUseQuickCredit = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const { loanAmt } = useSelector<IRootState, IPayAction>(state =>
    billPaymentResultSelector.payInfo(state),
  )
  const { disburseFee } = useSelector<IRootState, IBillPaymentAmountState>(
    state => state[BILL_PAYMENT_AMOUNT],
  )
  const { acctNo, acctTitle, lonAcctNo } = useSelector(billPaymentAmountSelector.selectedAccount)
  const curLoanAmt = getRupiahFormat(loanAmt)
  const curDisburseFee = getRupiahFormat(disburseFee ?? 0)
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const onCheckBoxChanged = () => {
    setIsConfirmed(prevState => !prevState)
  }

  const type = ALERT_LAYER_TYPE.BILL_PAYMENT_USE_QUICK_CREDIT
  const isOpen = useSelector<IRootState, ILayerState>(state => state[LAYER])[type]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(type))
  }, [dispatch, type])
  const onNavigate = useCallback(
    () =>
      Bridge.navigateTo(
        `${WEB_PAGE_NATIVE_ROUTES.DOCUMENT_CONTRACT_QUICK_CREDIT}/${lonAcctNo}`,
        false,
      ),
    [lonAcctNo],
  )

  const onConfirm = useCallback(() => {
    dispatch(payAsyncV3.post())

    /* Appsflyer */
    af.send(LOG_EVENT_NAME.APP_QCREDIT_DISBURSAL_REQUEST, {
      disbursaltype: LOG_EVENT_DISBURSAL_VALUE.DISBURSAL_TYPE,
    })

    dispatch(layerActions.close(type))
  }, [dispatch, type])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Alert isOpen={isOpen} isCenter>
        <Title>{$('billpayment_quick_credit_alert_title')}</Title>
        <Message>{$('billpayment_quick_credit_alert_desc')}</Message>
        <Definition.Wrapper>
          <dt>{$('billpayment_quick_credit_alert_amount')}</dt>
          <dd>{curLoanAmt}</dd>
          <dt>{$('billpayment_quick_credit_alert_fee')}</dt>
          <dd>{curDisburseFee}</dd>
          <dt>{$('billpayment_quick_credit_alert_account')}</dt>
          <dd>
            {acctTitle}
            <br />
            <Definition.PureAccount>{acctNo}</Definition.PureAccount>
          </dd>
        </Definition.Wrapper>
        {/* made radio button here */}
        <CheckBox
          id={'BillPaymentUseQuickCredit'}
          isChecked={isConfirmed}
          onChange={onCheckBoxChanged}
          additionalClassName={'chk-box__lb--gray'}
        >
          <span>
            <span>{$('qc_accept_desc')}</span>
            <span onClick={onNavigate} style={{ color: '#24C875', fontWeight: 'bold' }}>
              {$('qc_accept_desc_link')}
            </span>
          </span>
        </CheckBox>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose} isType2>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button
            onClick={onConfirm}
            isConfirm={isConfirmed}
            isType2={!isConfirmed}
            isDisabled={!isConfirmed}
          >
            {$('common_confirm_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Alert>
    </Layer.Wrapper>
  )
}

export const AlertLayerLoanBillPayment = (props: { payLogic: ActionFunctionAny<Action<any>> }) => {
  const $ = useTrans()
  const dispatch = useDispatch()

  const { loanAmt } = useSelector<IRootState, IPayAction>(state =>
    billPaymentResultSelector.payInfo(state),
  )
  const { disburseFee } = useSelector<IRootState, IBillPaymentAmountState>(
    state => state[BILL_PAYMENT_AMOUNT],
  )
  const { acctNo, acctTitle, lonAcctNo } = useSelector(billPaymentAmountSelector.selectedAccount)
  const curLoanAmt = getRupiahFormat(loanAmt)
  const curDisburseFee = getRupiahFormat(disburseFee ?? 0)
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const onCheckBoxChanged = () => {
    setIsConfirmed(prevState => !prevState)
  }
  const type = ALERT_LAYER_TYPE.BILL_PAYMENT_LOAN_DISBURSAL_CONFIRM
  const isOpen = useSelector<IRootState, ILayerState>(state => state[LAYER])[type]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(type))
  }, [dispatch, type])
  const onNavigate = useCallback(
    () =>
      Bridge.navigateTo(
        `${WEB_PAGE_NATIVE_ROUTES.DOCUMENT_CONTRACT_QUICK_CREDIT}/${lonAcctNo}`,
        false,
      ),
    [lonAcctNo],
  )
  const onConfirm = useCallback(() => {
    dispatch(props.payLogic())

    /* Appsflyer */
    af.send(LOG_EVENT_NAME.APP_QCREDIT_DISBURSAL_REQUEST, {
      disbursaltype: LOG_EVENT_DISBURSAL_VALUE.DISBURSAL_TYPE,
    })

    dispatch(layerActions.close(type))
  }, [dispatch, props, type])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Alert isOpen={isOpen} isType3>
        <Title>{$('billpayment_loan_alert_the_balance_is_low')}</Title>
        <Message>
          {$('billpayment_loan_alert_you_can_mack_up_the_insufficient_amount', [curLoanAmt])}
        </Message>
        <Definition.Wrapper>
          <dt>{$('billpayment_loan_alert_disbursal_amount')}</dt>
          <dd>{curLoanAmt}</dd>
          <dt>{$('billpayment_quick_credit_alert_fee')}</dt>
          <dd>{curDisburseFee}</dd>
          <dt>{$('billpayment_loan_alert_deposit_account')}</dt>
          <dd>
            {acctTitle}
            <br />
            <Definition.PureAccount>{acctNo}</Definition.PureAccount>
          </dd>
        </Definition.Wrapper>
        <CheckBox
          id={'LoanBillPayment'}
          isChecked={isConfirmed}
          onChange={onCheckBoxChanged}
          additionalClassName={'chk-box__lb--gray'}
        >
          <span>
            <span>{$('qc_accept_desc')}</span>
            <span onClick={onNavigate} style={{ color: '#24C875', fontWeight: 'bold' }}>
              {$('qc_accept_desc_link')}
            </span>
          </span>
        </CheckBox>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose} isType2>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button
            onClick={onConfirm}
            isConfirm={isConfirmed}
            isType2={!isConfirmed}
            isDisabled={!isConfirmed}
          >
            {$('common_confirm_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Alert>
    </Layer.Wrapper>
  )
}
